// src/stores/systemStatus.ts
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { statusService } from "@/services/statusService";
import { systemConfigService } from "@/services/systemConfigService";

export const useSystemStatusStore = defineStore("systemStatus", () => {
  // 存储所有服务的状态
  const serviceStatus = ref<Record<string, number>>({});
  // 记录最后一次更新时间
  const lastUpdated = ref<Date>(new Date());
  // 是否正在加载状态
  const loading = ref<boolean>(false);

  /**
   * 获取系统-服务映射关系
   */
  const getSystemServiceMapping = (): Record<string, string[]> => {
    const mapping: Record<string, string[]> = {};
    const systems = systemConfigService.systems;

    for (const [systemName, systemConfig] of Object.entries(systems)) {
      mapping[systemName] = systemConfig.services.map(
        (service) => service.name,
      );
    }

    return mapping;
  };

  // 计算每个系统的状态
  const systemStatus = computed<
    Record<string, { available: boolean; maintenance: boolean }>
  >(() => {
    const result: Record<string, { available: boolean; maintenance: boolean }> =
      {};
    const systemServiceMapping = getSystemServiceMapping();

    // 处理每个系统
    for (const [system, services] of Object.entries(systemServiceMapping)) {
      // 如果没有服务映射，默认可用
      if (services.length === 0) {
        result[system] = { available: true, maintenance: false };
        continue;
      }

      // 检查所有关联服务的状态
      const statuses = services.map(
        (service) => serviceStatus.value[service] || 1,
      );

      // 如果任何服务为0（下线），则系统不可用
      const hasDown = statuses.some((status) => status === 0);
      // 如果任何服务为3（维护中），则系统处于维护状态
      const hasMaintenance = statuses.some((status) => status === 3);

      result[system] = {
        available: !hasDown,
        maintenance: hasMaintenance,
      };
    }

    return result;
  });

  // 检查特定系统是否可用
  const isSystemAvailable = (systemName: string) => {
    return systemStatus.value[systemName]?.available ?? true;
  };

  // 检查特定系统是否处于维护状态
  const isSystemInMaintenance = (systemName: string) => {
    return systemStatus.value[systemName]?.maintenance ?? false;
  };

  // 刷新所有系统状态
  const refreshStatus = async () => {
    if (loading.value) return;

    loading.value = true;
    try {
      const status = await statusService.getSystemStatus();
      serviceStatus.value = status;
      lastUpdated.value = new Date();
    } catch (error) {
      console.error("获取系统状态失败:", error);
    } finally {
      loading.value = false;
    }
  };

  return {
    serviceStatus,
    systemStatus,
    lastUpdated,
    loading,
    isSystemAvailable,
    isSystemInMaintenance,
    refreshStatus,
    getSystemServiceMapping, // 导出映射函数供其他地方使用
  };
});
